/**
 * Prop Studio — the frame a prop's model is placed in, by the prop's kind.
 *
 * A FLOOR prop stands on the floor: the origin is its floor-centre, the wall
 * it stands against is at z = −d/2, the kit box stands on the floor. A HUNG
 * prop (on a SlatWall accessory) or a WALL prop (a towel bar) is HELD: the
 * origin is the hold point — a hook's prong, a shelf's top, a basket's floor,
 * the bar — out from the wall by the accessory's reach, the kit box hangs
 * `drop` feet below it and reaches `h − drop` above, and the model's
 * top-centre (something that hangs) or bottom-centre (something that sits)
 * goes to the origin. Both frames share their axes: x along the wall, y up,
 * z out of the wall into the room (README "Placement").
 */

import type { PropDef } from '../../calculations/staging/kits';
import { accessoryDef } from '../../data/slatwallAccessories';
import { Bounds, PropAnchor, PropTransform, placedBoundsFeet, stagedSizeFeet } from './placement';

/** How far out from the wall the procedural builders draw a held prop (propBuilder.ts). */
const BUILDER_HOLD_Z = 0.3;

export interface PropFrame {
  kind: 'floor' | 'held';
  /** The wall's plane, z in the prop's frame (feet). */
  wallZ: number;
  /** The kit's box in the prop's frame (feet). */
  box: Bounds;
  /** Held props: how far out from the wall the hold point is (feet). */
  holdZ: number;
  /** Which point of the model goes to the origin. */
  anchor: PropAnchor;
}

/** How far out from the wall a prop is held: its first accessory's reach, or what the builders assume. */
export function holdDepth(def: PropDef): number {
  const kind = def.hangsOn?.[0];
  if (!kind) return BUILDER_HOLD_Z;
  try {
    return accessoryDef(kind).holdAt.z;
  } catch {
    return BUILDER_HOLD_Z;
  }
}

/**
 * The frame as the room STAGES the model (stage.ts, DesignerHost's
 * stageVariants): a floor prop's body is the model's placed box -- or the
 * stage box of the user's own, centred on it -- and that body's back is what
 * stands against the wall, not the kit's (a prop made in Prop Studio has a
 * placeholder kit box). So the wall line, the box drawn, the clearance read
 * out and "Flush to wall" all measure from the staged body. Without a model
 * (or for a held prop), the kit's frame.
 */
export function stagedFrame(def: PropDef, modelBounds: Bounds | null | undefined, transform: PropTransform): PropFrame {
  const base = propFrame(def);
  if (!modelBounds) return base;
  const placed = placedBoundsFeet(modelBounds, transform);
  // A held prop's box is the model's placed box too: where the model hangs about
  // the hold point is what the room keeps clear and draws (heldAs) -- unless a
  // staging box of the user's own stands in for it.
  if (base.kind === 'held') return transform.stage ? base : { ...base, box: placed };
  const { w, d, h } = stagedSizeFeet(placed, transform);
  const cx = (placed.min[0] + placed.max[0]) / 2;
  const cz = (placed.min[2] + placed.max[2]) / 2;
  return { ...base, wallZ: cz - d / 2 - (def.clearBack ?? 0), box: { min: [cx - w / 2, 0, cz - d / 2], max: [cx + w / 2, h, cz + d / 2] } };
}

/**
 * A held prop (hung, on the wall) as its model's placed box: the box's size,
 * and how far it hangs below the hold point (`drop`; negative when the model
 * floats above it), the hold centred -- so the stand-in and the kit box are
 * drawn exactly where the model is, and move with it. Where the box is
 * centred along the wall and out from the hold is `heldOffset`.
 */
export function heldAs(def: PropDef, box: Bounds): PropDef {
  return { ...def, size: { w: box.max[0] - box.min[0], d: box.max[2] - box.min[2], h: box.max[1] - box.min[1] }, drop: -box.min[1], holdX: 0 };
}

/** Where a held model's box is centred in the prop's frame (feet: along the wall, out from the hold point): the stand-in is drawn there. */
export const heldOffset = (box: Bounds): [number, number] => [(box.min[0] + box.max[0]) / 2, (box.min[2] + box.max[2]) / 2];

export function propFrame(def: PropDef): PropFrame {
  const { w, d, h } = def.size;
  if (def.kind === 'floor' || def.kind === 'yard') {
    // The wall stands behind the box by what the prop keeps off it (clearBack).
    return { kind: 'floor', wallZ: -d / 2 - (def.clearBack ?? 0), box: { min: [-w / 2, 0, -d / 2], max: [w / 2, h, d / 2] }, holdZ: 0, anchor: 'floor' };
  }
  if (def.kind === 'ceiling') {
    // Hung from the ceiling: the origin is the point it is held at, the whole
    // prop below it; the backdrop wall stands clear behind (there is no wall in the room).
    const holdZ = d / 2 + 0.4;
    return { kind: 'held', wallZ: -holdZ, box: { min: [-w / 2, -h, -d / 2], max: [w / 2, 0, d / 2] }, holdZ, anchor: 'top' };
  }
  const drop = def.drop ?? 0;
  const holdZ = def.holdZ ?? holdDepth(def);
  // The hold point sits `holdX` right of the box's centre: in the frame (origin at the hold) the box is that far left.
  const hx = def.holdX ?? 0;
  return {
    kind: 'held',
    wallZ: -holdZ,
    box: { min: [-w / 2 - hx, -drop, -d / 2], max: [w / 2 - hx, h - drop, d / 2] },
    holdZ,
    // Something that hangs (a drop) is held by its top; something that sits (no drop) by its bottom.
    anchor: drop > 0 ? 'top' : 'floor',
  };
}
